import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from '../BaseTool';
import { BIMElement } from '../../core/types';

/**
 * Join Geometry Tool - BIM 2025 Style
 * 
 * Joins two elements so they clean up at their intersection.
 * Commonly used for:
 * - Wall-column joins
 * - Wall-wall joins
 * - Floor-wall joins
 * 
 * Workflow:
 * 1. Click first element
 * 2. Click second element
 * 3. Elements are joined at their intersection
 */
export class JoinToolClass extends BaseTool {
    id = 'JOIN';
    name = 'Join Geometry';
    cursor = 'crosshair';

    private firstElement: BIMElement | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const id = this.findElementIdAtPoint(event);
        if (!id) return;

        const element = this.context.bimStore.project.elements.get(id);
        if (!element) return;

        if (!this.firstElement) {
            // First click: Select first element
            this.firstElement = element;
            console.log('Join: First element selected -', element.category, element.id);

            this.context.appStore.setToolPreview({
                active: true,
                toolId: this.id,
                firstElementId: element.id
            });
        } else {
            // Second click: Join with second element
            if (element.id === this.firstElement.id) {
                console.warn('Join: Cannot join element to itself');
                return;
            }

            this.joinElements(this.firstElement, element);

            // Reset for next join (stay in tool)
            this.firstElement = null;
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerMove(): void { }
    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.reset();
            this.context?.appStore.setActiveTool('select');
        }
    }

    private joinElements(first: BIMElement, second: BIMElement): void {
        if (!this.context) return;

        // Check if elements can be joined
        const joinableCategories = ['Walls', 'Floors', 'Roofs', 'Ceilings', 'Structural Columns'];

        if (!joinableCategories.includes(first.category) || !joinableCategories.includes(second.category)) {
            console.warn('Join: Elements cannot be joined -', first.category, 'and', second.category);
            return;
        }

        // Create join relation
        const joinRelation = {
            type: 'JOIN' as const,
            targetId: second.id,
            data: {
                joinType: 'Butt', // Default join type
                createdAt: Date.now()
            }
        };

        // Add relation to first element
        const firstConstraints = first.constraints || [];
        this.context.bimStore.updateElement(first.id, {
            constraints: [...firstConstraints, joinRelation]
        });

        // Add reverse relation to second element
        const reverseRelation = {
            type: 'JOIN' as const,
            targetId: first.id,
            data: {
                joinType: 'Butt',
                createdAt: Date.now()
            }
        };

        const secondConstraints = second.constraints || [];
        this.context.bimStore.updateElement(second.id, {
            constraints: [...secondConstraints, reverseRelation]
        });

        console.log('Join: Joined', first.id, 'and', second.id);
    }

    private reset(): void {
        this.firstElement = null;
        this.context?.appStore.setToolPreview(null);
    }

    deactivate(): void {
        super.deactivate();
        this.reset();
    }
}

/**
 * Unjoin Geometry Tool - BIM 2025 Style
 * 
 * Removes the join between two elements.
 * 
 * Workflow:
 * 1. Click first element
 * 2. Click second element that is joined to it
 * 3. Join is removed
 */
export class UnjoinToolClass extends BaseTool {
    id = 'UNJOIN';
    name = 'Unjoin Geometry';
    cursor = 'crosshair';

    private firstElement: BIMElement | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const id = this.findElementIdAtPoint(event);
        if (!id) return;

        const element = this.context.bimStore.project.elements.get(id);
        if (!element) return;

        if (!this.firstElement) {
            // First click: Select first element
            this.firstElement = element;
            console.log('Unjoin: First element selected -', element.id);

            this.context.appStore.setToolPreview({
                active: true,
                toolId: this.id,
                firstElementId: element.id
            });
        } else {
            // Second click: Unjoin from second element
            this.unjoinElements(this.firstElement, element);

            // Reset
            this.firstElement = null;
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerMove(): void { }
    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.reset();
            this.context?.appStore.setActiveTool('select');
        }
    }

    private unjoinElements(first: BIMElement, second: BIMElement): void {
        if (!this.context) return;

        // Remove join from first element
        const firstConstraints = (first.constraints || []).filter(
            c => !(c.type === 'JOIN' && c.targetId === second.id)
        );
        this.context.bimStore.updateElement(first.id, { constraints: firstConstraints });

        // Remove join from second element
        const secondConstraints = (second.constraints || []).filter(
            c => !(c.type === 'JOIN' && c.targetId === first.id)
        );
        this.context.bimStore.updateElement(second.id, { constraints: secondConstraints });

        console.log('Unjoin: Unjoined', first.id, 'and', second.id);
    }

    private reset(): void {
        this.firstElement = null;
        this.context?.appStore.setToolPreview(null);
    }

    deactivate(): void {
        super.deactivate();
        this.reset();
    }
}

/**
 * Cut Geometry Tool - BIM 2025 Style
 * 
 * Cuts one element from another (creates an opening).
 * First element is the cutter, second element is being cut.
 * 
 * Commonly used for:
 * - Voids cutting through walls
 * - Beams cutting through walls
 * - Openings in floors
 * 
 * Workflow:
 * 1. Click cutting element
 * 2. Click element to be cut
 */
export class CutToolClass extends BaseTool {
    id = 'CUT_GEOMETRY';
    name = 'Cut Geometry';
    cursor = 'crosshair';

    private cutterElement: BIMElement | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const id = this.findElementIdAtPoint(event);
        if (!id) return;

        const element = this.context.bimStore.project.elements.get(id);
        if (!element) return;

        if (!this.cutterElement) {
            // First click: Select cutting element
            this.cutterElement = element;
            console.log('Cut: Cutter element selected -', element.category, element.id);

            this.context.appStore.setToolPreview({
                active: true,
                toolId: this.id,
                cutterElementId: element.id
            });
        } else {
            // Second click: Cut the second element
            if (element.id === this.cutterElement.id) {
                console.warn('Cut: Cannot cut element with itself');
                return;
            }

            this.cutElement(this.cutterElement, element);

            // Reset
            this.cutterElement = null;
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerMove(): void { }
    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.reset();
            this.context?.appStore.setActiveTool('select');
        }
    }

    private cutElement(cutter: BIMElement, target: BIMElement): void {
        if (!this.context) return;

        // Add cut relation to target element
        const cutRelation = {
            type: 'CONSTRAINT' as const,
            targetId: cutter.id,
            data: {
                constraintType: 'CUT',
                cutterId: cutter.id,
                createdAt: Date.now()
            }
        };

        const targetConstraints = target.constraints || [];
        this.context.bimStore.updateElement(target.id, {
            constraints: [...targetConstraints, cutRelation]
        });

        console.log('Cut: Element', cutter.id, 'cuts through', target.id);
    }

    private reset(): void {
        this.cutterElement = null;
        this.context?.appStore.setToolPreview(null);
    }

    deactivate(): void {
        super.deactivate();
        this.reset();
    }
}

/**
 * Uncut Geometry Tool - BIM 2025 Style
 * 
 * Removes a cut relationship between elements.
 */
export class UncutToolClass extends BaseTool {
    id = 'UNCUT_GEOMETRY';
    name = 'Uncut Geometry';
    cursor = 'crosshair';

    private firstElement: BIMElement | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const id = this.findElementIdAtPoint(event);
        if (!id) return;

        const element = this.context.bimStore.project.elements.get(id);
        if (!element) return;

        if (!this.firstElement) {
            this.firstElement = element;
            console.log('Uncut: First element selected -', element.id);

            this.context.appStore.setToolPreview({
                active: true,
                toolId: this.id,
                firstElementId: element.id
            });
        } else {
            this.uncutElements(this.firstElement, element);

            this.firstElement = null;
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerMove(): void { }
    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.reset();
            this.context?.appStore.setActiveTool('select');
        }
    }

    private uncutElements(first: BIMElement, second: BIMElement): void {
        if (!this.context) return;

        // Remove cut constraint from both elements
        const removeConstraint = (element: BIMElement, otherId: string) => {
            const constraints = (element.constraints || []).filter(c => {
                if (c.type === 'CONSTRAINT' && c.data?.constraintType === 'CUT') {
                    return c.targetId !== otherId && c.data?.cutterId !== otherId;
                }
                return true;
            });
            this.context!.bimStore.updateElement(element.id, { constraints });
        };

        removeConstraint(first, second.id);
        removeConstraint(second, first.id);

        console.log('Uncut: Removed cut relationship between', first.id, 'and', second.id);
    }

    private reset(): void {
        this.firstElement = null;
        this.context?.appStore.setToolPreview(null);
    }

    deactivate(): void {
        super.deactivate();
        this.reset();
    }
}

/**
 * Switch Join Order Tool - BIM 2025 Style
 * 
 * Reverses the order of element joining (which element cuts the other).
 * 
 * Workflow:
 * 1. Select two joined elements.
 * 2. Their join order is swapped.
 */
export class SwitchJoinOrderToolClass extends BaseTool {
    id = 'SWITCH_JOIN_ORDER';
    name = 'Switch Join Order';
    cursor = 'arrow';

    private firstElement: BIMElement | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const id = this.findElementIdAtPoint(event);
        if (!id) return;

        const element = this.context.bimStore.project.elements.get(id);
        if (!element) return;

        if (!this.firstElement) {
            this.firstElement = element;
            console.log('Switch Join Order: First element selected -', element.id);
            this.context.appStore.setToolPreview({
                active: true,
                toolId: this.id,
                firstElementId: element.id
            });
        } else {
            this.switchJoinOrder(this.firstElement, element);
            this.firstElement = null;
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerMove(): void { }
    onPointerUp(): void { }

    private switchJoinOrder(first: BIMElement, second: BIMElement): void {
        // Logic: Find the JOIN constraint between them and toggle some 'primary' flag?
        // Or simply remove and re-add in reverse order if order implies precedence?
        // In our simple JOIN constraint, we stored: targetId: second.id.
        // It's a bidirectional link.
        // "Switch Join Order" usually implies one element cuts the other.
        // We will assume the existence of a 'cutting' property or similar in the Join data.
        // If not, we'll mark one as 'primary'.

        if (!this.context) return;

        // Helper to update the join relation
        const updateJoin = (element: BIMElement, targetId: string, isPrimary: boolean) => {
            const constraints = element.constraints || [];
            const newConstraints = constraints.map(c => {
                if (c.type === 'JOIN' && c.targetId === targetId) {
                    return { ...c, data: { ...c.data, isPrimary } };
                }
                return c;
            });
            this.context!.bimStore.updateElement(element.id, { constraints: newConstraints });
        };

        // For now, let's just toggle a 'isPrimary' flag. 
        // Real implementation would depend on the CSG engine interpreting this flag.

        // Check current state (mock)
        // Assume First was Primary, now Second is Primary.
        updateJoin(first, second.id, false);
        updateJoin(second, first.id, true);

        console.log('Switch Join Order: Switched', first.id, 'and', second.id);
    }
}
